import { useQuery, UseQueryResult } from '@tanstack/react-query';
import config from 'src/config';
import ApiResponse from '../ApiResponse';
import ApiError from '../ApiError';
import { SupplierDTO } from './useSupplier';

interface PageInfo {
  hasNextPage: boolean;
  endCursor: string | null;
}

interface QueryParams {
  first?: number;
  search?: string;
  module?: string;
}

interface PaginatedApiResponse {
  edges: SupplierDTO[];
  pageInfo: PageInfo;
}

/**
 * React Query hook that fetches ALL suppliers by making a single API call.
 * TODO: Re-implement proper pagination logic to handle large datasets efficiently.
 *
 * This is useful when you need all suppliers for components like:
 * - Autocomplete/dropdowns
 * - Export functionality
 * - Analytics/reporting
 *
 * For table views with pagination, use the regular `useSuppliers` hook instead.
 *
 * @example
 * // Get all suppliers for an autocomplete
 * const { data: allSuppliers, isLoading, error } = useAllSuppliers();
 *
 * @example
 * // Search across all suppliers
 * const { data: filteredSuppliers } = useAllSuppliers({ search: 'ACME' });
 *
 * @example
 * // Fetch with custom page size (for performance tuning)
 * const { data: allSuppliers } = useAllSuppliers({ first: 100 });
 */
const useAllSuppliers = (
  params?: QueryParams,
  enabled = true,
): UseQueryResult<SupplierDTO[], ApiError> => {
  return useQuery<SupplierDTO[], ApiError>({
    queryKey: ['suppliers', 'all', params],
    queryFn: async (): Promise<SupplierDTO[]> => {
      const queryParams = new URLSearchParams();
      queryParams.append('first', '1000'); // Large page size to get all suppliers

      if (params?.search) {
        queryParams.append('search', params.search);
      }

      if (params?.module) {
        queryParams.append('module', params.module);
      }

      const url = `${
        config.apiBaseUrl
      }/accounts-payable/suppliers?${queryParams.toString()}`;

      const response = await fetch(url, {
        credentials: 'include',
      });

      if (!response.ok) {
        const errorResponse: ApiError = await response.json();
        throw new Error(errorResponse.message);
      }

      const result: ApiResponse<PaginatedApiResponse> = await response.json();

      // Return all suppliers from the single page
      return result.data.edges;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
};

export default useAllSuppliers;
